import { API_BASE_URL as BASE } from '@/lib/apiBase'
import type { PostalAddress } from '@/lib/address/address-rules'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
/** A partner's address as the shipment snapshotted it (Customer model fields). */
export interface ShipmentDestination {
  address: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  country: string | null
  /** The structured address on the day (z8nrz7epeq); absent on older shipments. */
  postal?: PostalAddress
  /** The lines it printed as on the day; absent on older shipments. */
  lines?: string[]
}

/** One product on a shipment, with the slot it was taken from. */
export interface ShipmentItem {
  id: string
  productId: string
  din: string
  productCode: string
  fromStorageNodeId: string | null
  fromPath: string | null
}

/**
 * One signed dispatch of products to a partner (z8nrz7eaex). The partner's
 * code, name and address are a SNAPSHOT taken at shipping time, so a later
 * edit to the partner never rewrites where a product went.
 */
export interface Shipment {
  id: string
  number: string
  partnerId: string
  courierId: string | null
  partnerCode: string
  partnerName: string
  destination: ShipmentDestination
  courierCode: string | null
  courierName: string | null
  trackingNumber: string | null
  comments: string | null
  signedBy: string
  signerName: string | null
  signatureMeaning: string
  shippedAt: string
  items: ShipmentItem[]
}

export interface CreateShipmentPayload {
  productIds: string[]
  partnerId: string
  courierId?: string
  trackingNumber?: string
  comments?: string
  /** Single-use proof the signer re-entered their password (lib/signingToken.ts). */
  signingToken: string
  signatureMeaning: string
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const body = JSON.parse(text) as { message?: string | string[] }
      message = Array.isArray(body.message) ? body.message.join('. ') : (body.message ?? text)
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

// Tenant-scoped: authHeaders injects X-Tenant-Id for the selected tenant.
export const shipmentsApi = {
  create: (payload: CreateShipmentPayload) =>
    request<Shipment>('/shipments', { method: 'POST', body: JSON.stringify(payload) }),

  list: (productId?: string) =>
    request<Shipment[]>(productId ? `/shipments?productId=${encodeURIComponent(productId)}` : '/shipments'),
}
